/**
 * Shared color constants and utility functions for sentiment and rating visualization
 * across all chart types (horizontal bar, line, bubble, scatter, treemap, heatmap, stacked bar)
 */

/** A panel's display defaults, for readers that build a panel from a request. */
export { DEFAULT_PANEL_FIELDS } from 'features/charts-v2/constants/panel-defaults'

export enum SentimentColorGroup {
  POSITIVE = 'positive',
  NEUTRAL = 'neutral',
  NEGATIVE = 'negative',
}

/**
 * Sentiment threshold values
 * - Values > 0.3 are considered positive
 * - Values < -0.3 are considered negative
 * - Values between -0.3 and 0.3 are considered neutral
 */
export const SENTIMENT_THRESHOLDS = {
  POSITIVE: 0.3,
  NEGATIVE: -0.3,
} as const

/**
 * Solid colors for sentiment-based bars, segments, and elements
 */
export const SENTIMENT_ELEMENT_COLORS = {
  [SentimentColorGroup.POSITIVE]: 'rgb(16, 185, 129)', // Green
  [SentimentColorGroup.NEUTRAL]: 'rgb(245, 158, 11)', // Yellow/Amber
  [SentimentColorGroup.NEGATIVE]: 'rgb(239, 68, 68)', // Red
} as const

/**
 * Semi-transparent colors for sentiment background zones (ReferenceArea)
 */
export const SENTIMENT_ZONE_COLORS = {
  [SentimentColorGroup.POSITIVE]: 'rgba(34, 197, 94, 0.15)', // Light green
  [SentimentColorGroup.NEUTRAL]: 'transparent',
  [SentimentColorGroup.NEGATIVE]: 'rgba(239, 68, 68, 0.15)', // Light red
} as const

/**
 * Determine sentiment color group based on numeric value
 */
export const getSentimentColorGroup = (value: number): SentimentColorGroup => {
  if (value > SENTIMENT_THRESHOLDS.POSITIVE) return SentimentColorGroup.POSITIVE
  if (value < SENTIMENT_THRESHOLDS.NEGATIVE) return SentimentColorGroup.NEGATIVE
  return SentimentColorGroup.NEUTRAL
}

/**
 * Get bar/element color for a sentiment value
 * Uses the 3-bucket system: green (> 0.3), yellow (-0.3 to 0.3), red (< -0.3)
 */
export const getSentimentBarColor = (value: number): string =>
  SENTIMENT_ELEMENT_COLORS[getSentimentColorGroup(value)]

/**
 * 9-step sentiment color scale across [-1, 1].
 * Three shades each of red / amber / green. Used for fine-grained sentiment
 * coloring (e.g. keyword chips) where the 3-bucket system loses too much detail.
 */
export const SENTIMENT_SCALE_COLORS_9 = [
  'rgb(220, 38, 38)', // red darker
  'rgb(239, 68, 68)', // red base
  'rgb(248, 113, 113)', // red lighter
  'rgb(217, 119, 6)', // amber darker
  'rgb(245, 158, 11)', // amber base
  'rgb(251, 191, 36)', // amber lighter
  'rgb(52, 211, 153)', // green lighter
  'rgb(16, 185, 129)', // green base
  'rgb(5, 150, 105)', // green darker
] as const

export const getSentimentScaleColor = (value: number): string => {
  const clamped = Math.max(-1, Math.min(1, value))
  const idx = Math.min(8, Math.floor(((clamped + 1) / 2) * 9))
  return SENTIMENT_SCALE_COLORS_9[idx] as string
}

/**
 * Rating threshold values (1-5 scale)
 * - Values >= 4 are considered positive
 * - Values < 3 are considered negative
 * - Values between 3 and 4 are considered neutral
 */
export const RATING_THRESHOLDS = {
  POSITIVE: 4,
  NEGATIVE: 3,
} as const

/**
 * Color scheme for rating stacking segments (1-5 star breakdown)
 * Red-to-green gradient matching sentiment colors
 */
export const RATING_COLORS: Record<string, string> = {
  '1': '#ef4444', // Red (1 star - negative)
  '2': '#f97316', // Orange (2 stars)
  '3': '#f59e0b', // Yellow/Amber (3 stars - neutral)
  '4': '#84cc16', // Light Green (4 stars)
  '5': '#10b981', // Green (5 stars - positive)
  '1 Star': '#ef4444',
  '2 Stars': '#f97316',
  '3 Stars': '#f59e0b',
  '4 Stars': '#84cc16',
  '5 Stars': '#10b981',
} as const

/**
 * Semi-transparent colors for rating background zones (ReferenceArea)
 */
export const RATING_ZONE_COLORS = {
  negative: 'rgba(239, 68, 68, 0.15)', // Light red
  positive: 'rgba(34, 197, 94, 0.15)', // Light green
} as const

/**
 * Cap for stacking categories on stacked bar charts. Beyond this, the chart
 * keeps the top N (by total) and aggregates the remainder into a single bucket
 * so we don't render hundreds of segments per bar (browser freeze). The legend
 * mirrors this cap so it doesn't list categories that aren't actually drawn.
 */
export const MAX_STACKING_CATEGORIES = 15
export const STACKING_OVERFLOW_LABEL = 'Other'

/**
 * Maximum number of raw datapoints (rows in Chart.data) the chart, legend, and
 * data table will attempt to render. Exceeding this limit causes all three
 * surfaces to show a "too many datapoints" error instead of painting, preventing
 * browser freezes/crashes at large row counts.
 */
export const MAX_RENDERABLE_DATAPOINTS = 1000

/**
 * Minimum drawn length, in px, of a bar with a non-zero value.
 *
 * A bar's click target IS its drawn rect — `onClick` sits on `<Bar>` — and
 * recharts omits a zero-length rect from the DOM entirely. So a bar too short
 * to occupy a pixel is both invisible and unclickable, and the AI-summary panel
 * can't be opened for it (PROD-5286). Two independent things produce one:
 * a value sitting on the axis floor, and a value that is a tiny fraction of the
 * largest one (a count of 30 beside 20,000 is 0.7px on a ~450px axis) — no axis
 * change can fix the second, which is why this floor exists alongside
 * `computeSnappedBarDomain`'s headroom.
 */
export const MIN_BAR_POINT_SIZE = 4

/**
 * `minPointSize` callback for recharts `<Bar>`: floor non-zero bars at
 * MIN_BAR_POINT_SIZE, and keep a GENUINE zero at zero — a real 0 must not draw
 * a stub that reads as "a small amount". Recharts types the callback's value as
 * nullable, and a missing value is treated the same as zero: nothing drawn.
 */
export const barMinPointSize = (value: number | null | undefined): number =>
  value ? MIN_BAR_POINT_SIZE : 0

/**
 * Solid bar colors for each star rating bucket (1–5).
 * Keyed by integer star count so the legend and bar coloring share one source.
 */
export const RATING_BAR_COLORS: Record<number, string> = {
  5: '#10b981', // Green
  4: '#84cc16', // Light Green
  3: '#f59e0b', // Yellow/Amber
  2: '#f97316', // Orange
  1: '#ef4444', // Red
} as const

/**
 * Map a continuous rating value (1–5 scale) to its integer star bucket (1–5).
 */
export const getRatingBucket = (value: number): 1 | 2 | 3 | 4 | 5 => {
  if (value >= 4.5) return 5
  if (value >= 3.5) return 4
  if (value >= 2.5) return 3
  if (value >= 1.5) return 2
  return 1
}

/**
 * Get bar/element color for a rating value (continuous 1-5 scale)
 * Uses the same 5-level color scheme as RATING_COLORS
 */
export const getRatingBarColor = (value: number): string =>
  RATING_BAR_COLORS[getRatingBucket(value)]!
